/**
 * The roster: every Naurist account, as the panel reads it.
 *
 * The list endpoint pages on the server, so the whole roster is never in the
 * browser at once — this console is meant to survive the day there are tens of
 * thousands of accounts, and a client-side filter over all of them is not.
 * Which means the tab, the search term and the page number all travel as query
 * params and the total comes back with each page; see `useUsers`, which is
 * what keeps them in step.
 *
 * Everything here is live, and there is nothing left that is not:
 *
 * - `/api/v1/backoffice/users/` — the roster, paged and filtered and counted.
 * - `/api/v1/backoffice/users/<workspace_id>/` — one account, and one page of
 *   whichever of its two lists was asked for.
 * - `/api/v1/backoffice/users/<workspace_id>/reactivate/` — mails a lapsed
 *   account an invitation back.
 */

import { API_ENDPOINTS, api } from "../../../lib/api";
import { formatDate, formatSince } from "../userFormat";
import type {
  AccountType,
  AdminUser,
  ClientStatus,
  Invoice,
  InvoiceStatus,
  RosterCounts,
  UserClient,
  UserDetail,
  UserPlan,
  UserStatus,
  UserTabId,
  UsersPage,
} from "../types/users.types";

export interface UsersQuery {
  /** 1-based, the way the pagination control counts. */
  page: number;
  pageSize: number;
  tab: UserTabId;
  /** The Account type dropdown. `any` travels as the endpoint's own `all`. */
  accountType: AccountType;
  /** Matched against the name and the address. Empty means no filter. */
  search: string;
}

/** The wire shape of one row, before it is mapped to `AdminUser`. */
interface UserResponse {
  /**
   * The account, which is what the panel calls a user: one workspace, one
   * practice. `owner_id` beside it is the person who made it, and the two are
   * not interchangeable — this is the one the roster is a list of, so it is
   * the id a row carries and navigates by. The day the detail route exists,
   * whichever of the two it keys on is the one to send it.
   */
  workspace_id: number;
  owner_id?: number | null;
  name?: string | null;
  email?: string | null;
  /** Already the panel's own words — "Free trial", "Monthly", "Yearly". */
  plan?: string | null;
  plan_expires_at?: string | null;
  /** One line — "Mumbai, Maharashtra" — rather than city and country apart. */
  location?: string | null;
  /** A day, `YYYY-MM-DD`. */
  joined_on?: string | null;
  clients?: number | null;
  /** An instant, ISO 8601 with an offset, or null for an account nobody has
   *  opened yet. */
  last_active?: string | null;
  /** snake_case, unlike `plan` — mapped by `toStatus`. */
  status?: string | null;
}

/** Standard paged envelope, plus the roster-wide counts. */
interface UsersListResponse {
  count?: number | null;
  next?: string | null;
  previous?: string | null;
  tab_counts?: {
    all?: number | null;
    free_trial?: number | null;
    pro?: number | null;
  } | null;
  results?: UserResponse[] | null;
}

/** Unknown values fall back rather than throwing — a plan the panel has not
 *  been taught about should not blank the whole table. */
const toPlan = (value: string | null | undefined): UserPlan =>
  value === "Monthly" || value === "Yearly" ? value : "Free trial";

/**
 * The API's word for how an account stands, in the panel's.
 *
 * Only `trial_active` and `subscription_active` have been seen on the wire;
 * the rest are the obvious complements, written out so a value that does turn
 * up lands somewhere sensible rather than on the fallback. Adding one is a
 * line here.
 *
 * There is nothing for an expired subscription other than `Signed up`, and
 * that is deliberate rather than a gap: the panel has no `Expired` status by
 * design — whether a plan has run out is a question about `expiresOn`, and a
 * status word for it would be a second copy of that fact, free to disagree.
 * See the note on `UserStatus`.
 */
const STATUS_WORDS: Record<string, UserStatus> = {
  signed_up: "Signed up",
  trial_active: "Trial active",
  trial_ended: "Trial ended",
  trial_expired: "Trial ended",
  ending_soon: "Ending soon",
  subscription_ending_soon: "Ending soon",
  subscription_expiring: "Ending soon",
  active: "Active",
  subscription_active: "Active",
  expired: "Signed up",
  subscription_expired: "Signed up",
};

/**
 * A status the panel has not been taught about reads as `Signed up` — the
 * quietest of the five, and the only one that claims nothing. The fallback
 * used to be `Active`, which would have had an unrecognised word assert that
 * somebody is paying.
 */
const toStatus = (value: string | null | undefined): UserStatus =>
  STATUS_WORDS[(value ?? "").toLowerCase()] ?? "Signed up";

const toUser = (row: UserResponse): AdminUser => ({
  id: row.workspace_id,
  name: row.name?.trim() || "Unnamed account",
  email: row.email?.trim() || "",
  // Not on this endpoint, and not in this table either — see `AdminUser.phone`.
  phone: "—",
  plan: toPlan(row.plan),
  location: row.location?.trim() || "—",
  joinedOn: formatDate(row.joined_on),
  clients: row.clients ?? null,
  lastActive: formatSince(row.last_active),
  // Left null rather than dashed: the column that prints it is what decides
  // how a plan with no end date reads.
  expiresOn: row.plan_expires_at ? formatDate(row.plan_expires_at) : null,
  status: toStatus(row.status),
});

const toCounts = (counts: UsersListResponse["tab_counts"]): RosterCounts => ({
  all: counts?.all ?? undefined,
  freeTrial: counts?.free_trial ?? undefined,
  pro: counts?.pro ?? undefined,
});

/** The dropdown's words, in the endpoint's. `any` is a value the server knows
 *  rather than a parameter left off, so every request says what it is asking
 *  for. */
const PLAN_PARAM: Record<AccountType, string> = {
  any: "all",
  trial: "free_trial",
  monthly: "monthly",
  yearly: "yearly",
};

/** The tab bar's, likewise. The endpoint also takes `active`, which no tab
 *  asks for — the roster's split is everyone against the ones that lapsed. */
const STATUS_PARAM: Record<UserTabId, string> = {
  all: "all",
  expired: "expired",
};

/**
 * One page of the roster, filtered and counted by the server.
 *
 * Everything that narrows it travels as a query param — the tab, the plan, the
 * search term, the page — because the browser never holds the whole roster to
 * narrow. The counts come back with the page rather than from a call of their
 * own: they describe the roster rather than the query, so they do not move
 * when the search box does.
 *
 * There is no fixture branch here any more. This is the one users endpoint
 * that exists.
 */
export const listUsers = async ({
  page,
  pageSize,
  tab,
  accountType,
  search,
}: UsersQuery): Promise<UsersPage> => {
  const data = await api.get<UsersListResponse>(API_ENDPOINTS.users.list, {
    query: {
      status: STATUS_PARAM[tab],
      plan: PLAN_PARAM[accountType],
      // The only one left off when it is empty: a blank search is the absence
      // of a filter, where a blank plan would be a value the server rejects.
      search: search.trim() || undefined,
      page,
      page_size: pageSize,
    },
  });

  return {
    users: (data.results ?? []).map(toUser),
    total: data.count ?? 0,
    counts: toCounts(data.tab_counts),
  };
};

/** All the route answers with: `{ "detail": "Reactivation email sent." }`. */
interface ReactivateResponse {
  detail?: string | null;
}

/**
 * Asks the server to mail a lapsed account an invitation back.
 *
 * What the Expired tab's "Reactivate" does. Nothing is sent with it — the
 * wording of the mail is the server's, so the console cannot end up owning a
 * second copy of the copy.
 *
 * Keyed on the workspace, which is the id every roster row already carries.
 * The `detail` sentence that comes back is read rather than shown: the row
 * says "Reminded" in its own words, in the place the reader just clicked, and
 * a server sentence repeated there would say the same thing twice. A failure
 * is the other way round — `api` turns it into an `ApiError` carrying the
 * server's own message, and the page prints that, because a mail that did not
 * go out is worth the server's exact reason.
 */
export const reactivateUser = async (workspaceId: number): Promise<void> => {
  await api.post<ReactivateResponse>(
    API_ENDPOINTS.users.reactivate(workspaceId)
  );
};

/* -------------------------------------------------------------------------
   One account: the detail screen
   ---------------------------------------------------------------------- */

/**
 * The account itself, sent with either view — see `API_ENDPOINTS.users.detail`.
 *
 * "Practitioner" is the API's word for the person whose workspace this is. The
 * panel calls them a user, because from a console that also lists their
 * clients, "user" is the one who signed up to Naurist.
 */
interface PractitionerResponse {
  workspace_id: number;
  name?: string | null;
  /** snake_case, the same vocabulary the roster uses — see `toStatus`. */
  status?: string | null;
  plan?: string | null;
  /** Upper case, underscored — "FEMALE", "PREFER_NOT_TO_SAY". */
  gender?: string | null;
  /** Counted by the server. There is no date of birth on this record. */
  age?: number | null;
  member_since?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  address?: string | null;
}

/** The paged envelope both views answer in, with the account on top. */
interface WorkspaceResponse<T> {
  practitioner?: PractitionerResponse | null;
  view?: string | null;
  count?: number | null;
  next?: string | null;
  previous?: string | null;
  results?: T[] | null;
}

interface ClientResponse {
  public_id: string;
  name?: string | null;
  /** Already one line — "Andheri West, Mumbai". */
  location?: string | null;
  status?: string | null;
  joined_on?: string | null;
}

interface InvoiceResponse {
  public_id: string;
  invoice_number?: string | null;
  /** A decimal string, for the reason the dashboard's figures are. */
  amount?: string | number | null;
  currency?: string | null;
  date?: string | null;
  status?: string | null;
}

/** One page of one of an account's lists, with the account it belongs to. */
export interface WorkspacePage<T> {
  user: UserDetail;
  rows: T[];
  /** How many rows there are across every page. */
  total: number;
}

/** "FEMALE" as something worth printing. Underscores become spaces, so a
 *  multi-word answer does not arrive as one shouted token. */
const toGender = (value: string | null | undefined): string => {
  const word = value?.trim().replace(/_/g, " ");
  if (!word) return "—";
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

const toUserDetail = (row: PractitionerResponse): UserDetail => ({
  id: row.workspace_id,
  name: row.name?.trim() || "Unnamed account",
  status: toStatus(row.status),
  plan: toPlan(row.plan),
  gender: toGender(row.gender),
  age: typeof row.age === "number" && Number.isFinite(row.age) ? row.age : null,
  joinedOn: formatDate(row.member_since),
  phone: row.phone?.trim() || "—",
  // Falls back to the phone rather than to a dash: the two are the same number
  // often enough that an account which gave only one has almost certainly given
  // the one it can be reached on.
  whatsapp: row.whatsapp?.trim() || row.phone?.trim() || "—",
  email: row.email?.trim() || "—",
  address: row.address?.trim() || "—",
});

/**
 * Pulls the account off either view's answer.
 *
 * A body with no `practitioner` in it is a malformed answer rather than a
 * missing account, so it throws a plain error: the screen reports a failure
 * worth retrying instead of telling somebody the record is gone. A record that
 * really is gone arrives as a 404 from the server, which `api` has already
 * turned into an `ApiError` by the time this runs.
 */
const readWorkspace = <Wire, Row>(
  data: WorkspaceResponse<Wire>,
  toRow: (row: Wire) => Row
): WorkspacePage<Row> => {
  if (!data.practitioner) {
    throw new Error("The account was missing from the server's answer.");
  }

  return {
    user: toUserDetail(data.practitioner),
    rows: (data.results ?? []).map(toRow),
    total: data.count ?? 0,
  };
};

/** A client is on the books or they are not — see `ClientStatus`. */
const toClientStatus = (value: string | null | undefined): ClientStatus =>
  value?.trim().toLowerCase() === "active" ? "Active" : "Inactive";

const toClient = (row: ClientResponse): UserClient => ({
  id: row.public_id,
  name: row.name?.trim() || "Unnamed client",
  location: row.location?.trim() || "—",
  joinedOn: formatDate(row.joined_on),
  status: toClientStatus(row.status),
});

/** Anything that is not the live one has been and gone. There are only the two
 *  states, and of the two, "this is the one running now" is the claim worth
 *  making carefully. */
const toInvoiceStatus = (value: string | null | undefined): InvoiceStatus =>
  value?.trim().toLowerCase() === "active" ? "Active" : "Expired";

const toInvoice = (row: InvoiceResponse): Invoice => ({
  id: row.invoice_number?.trim() || row.public_id,
  publicId: row.public_id,
  amount:
    typeof row.amount === "string" ? Number(row.amount) || 0 : row.amount ?? 0,
  date: formatDate(row.date),
  status: toInvoiceStatus(row.status),
});

/**
 * The Details tab: the account, and one page of the clients on its roster.
 *
 * Paged on the server, unlike the version of this that came before it — a
 * practice with three hundred clients is a practice this console should not be
 * holding in the browser to show ten of them.
 */
export const getWorkspaceClients = async (
  workspaceId: number,
  page: number,
  pageSize: number
): Promise<WorkspacePage<UserClient>> => {
  const data = await api.get<WorkspaceResponse<ClientResponse>>(
    API_ENDPOINTS.users.detail(workspaceId),
    { query: { view: "details", page, page_size: pageSize } }
  );

  return readWorkspace(data, toClient);
};

/** The Billing history tab: the same account, and one page of its invoices. */
export const getWorkspaceBilling = async (
  workspaceId: number,
  page: number,
  pageSize: number
): Promise<WorkspacePage<Invoice>> => {
  const data = await api.get<WorkspaceResponse<InvoiceResponse>>(
    API_ENDPOINTS.users.detail(workspaceId),
    { query: { view: "billing_history", page, page_size: pageSize } }
  );

  return readWorkspace(data, toInvoice);
};

/**
 * Saves one invoice to the reader's machine.
 *
 * The file is fetched rather than linked to, because the endpoint is behind
 * the admin session and an `<a href>` would be a plain unauthenticated GET.
 * The object URL is revoked straight after the click — it holds the whole file
 * in memory until it is, and this console is left open all day.
 *
 * Takes the whole invoice rather than an id: the route is keyed on
 * `publicId`, and the file is saved under the number printed on it, which are
 * two different fields.
 *
 * ## Why the Accept header is not just `application/pdf`
 *
 * Because that is a 406. The route is a DRF view, and DRF negotiates content
 * before it authenticates: it matches the Accept header against the renderers
 * it has registered, and there is no PDF renderer among them — the view builds
 * the file itself and returns it past the renderer entirely. So asking for
 * `application/pdf` and nothing else is asking for something DRF cannot
 * promise, and it refuses the request before reaching the code that would have
 * answered it.
 *
 * The wildcard after it is what gets through: negotiation settles on the JSON
 * renderer, which the view then ignores. The PDF is still named first, so the
 * day a PDF renderer is registered this asks for it by preference rather than
 * by accident.
 */
export const downloadInvoice = async (
  workspaceId: number,
  invoice: Invoice
): Promise<void> => {
  const blob = await api.getBlob(
    API_ENDPOINTS.users.invoiceFile(workspaceId, invoice.publicId),
    { headers: { Accept: "application/pdf, */*" } }
  );

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${invoice.id}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
